import { v4 as uuidv4 } from "uuid";

import Websocket, { IWebsocketHandler } from "@skydio/comms/src/ws/reconnecting_websocket";

import auth_pb from "@skydio/pbtypes/pbtypes/vehicle/infrastructure/flight_deck/auth_pb";
import runmode_pb from "@skydio/pbtypes/pbtypes/vehicle/infrastructure/flight_deck/runmode_pb";
import server_pb from "@skydio/pbtypes/pbtypes/vehicle/infrastructure/flight_deck/server_pb";
import iot_management_pb from "@skydio/pbtypes/pbtypes/tools/cloud_api/iot_management_pb";

import { isSimulatorRunModesResponse } from "./types/http";
import type { ISimulatorRunModesResponse } from "./types/http";
import type { ValueType } from "@skydio/core";

async function parseResponse(response: Response) {
  switch (response.headers.get("Content-Type")) {
    case "application/json":
      return response.json();
    case "application/protobuf":
    case "application/x-protobuf":
      const buffer: ArrayBuffer = await response.arrayBuffer();
      return server_pb.ApiResponse.deserializeBinary(new Uint8Array(buffer));
    default:
      throw Error("Unknown content type");
  }
}

interface IJsonError {
  type: string;
  message: string;
}
interface IJsonResponse {
  data: object;
  error?: IJsonError;
  extraData?: any;
}

interface IExtendedError extends Error {
  response: string;
  type: string;
  extraDataProto?: any;
  extraDataJson?: any;
}

function parseData(data: IJsonResponse | server_pb.ApiResponse): any {
  let errorType: string = "";
  let errorMsg: string = "";
  let extraDataProto: any;
  let extraDataJson: any;
  let isProto: boolean = false;
  let hasError: boolean;

  if (data instanceof server_pb.ApiResponse) {
    // protobuf response
    isProto = true;
    hasError = data.hasError();
    if (hasError) {
      const error = data.getError();
      errorType = (error as server_pb.Error).getType();
      errorMsg = (error as server_pb.Error).getMessage();
    }
    extraDataProto = data.getExtraData();
  } else {
    // json response
    hasError = Boolean(data.error);
    if (hasError) {
      errorType = (data.error as IJsonError).type;
      errorMsg = (data.error as IJsonError).message;
    }
    extraDataJson = data.extraData ? data.extraData : null;
  }

  if (hasError) {
    const error = new Error(`[${errorType}]: ${errorMsg}`) as IExtendedError;
    error.response = errorMsg;
    error.type = errorType;
    error.extraDataProto = extraDataProto;
    error.extraDataJson = extraDataJson;
    throw error;
  }

  return isProto ? (data as server_pb.ApiResponse).getData() : (data as IJsonResponse).data;
}

function sleep(time: number): Promise<void> {
  return new Promise(resolve => setTimeout(resolve, time));
}

type IStateChangeCallback = (client: FlightDeckClient) => void;

type FlightDeckRequestOptions<ResponseType> = {
  desiredLevel?: ValueType<auth_pb.Auth.AccessLevelMap>;
  additionalAcceptedLevels?: ValueType<auth_pb.Auth.AccessLevelMap>[];
  commandeer?: boolean;
  protobufDeserializer?: (bytes: Uint8Array) => ResponseType;
  raw?: boolean;
};

export default class FlightDeckClient {
  public readonly clientId: string;

  public websockets: Record<string, Websocket> = {};

  public authToken: string | null = null;
  public authLevel: ValueType<auth_pb.Auth.AccessLevelMap> = auth_pb.Auth.AccessLevel.NO_AUTH;

  private authIsLoading: boolean = false;
  private stateChangedCallbacks: IStateChangeCallback[] = [];
  private loadingRequests: Set<string> = new Set();
  private requestHeaders: Record<string, string> = {};

  constructor(
    public readonly url: string,
    public readonly websocketUrl: string,
    clientId: string | null,
    public readonly credentials: string
  ) {
    this.clientId = clientId || uuidv4();
  }

  public dispose = () => {
    Object.keys(this.websockets).forEach(this.stopWebsocket);
  };

  public addStateChangedCallback = (callback: IStateChangeCallback) => {
    this.stateChangedCallbacks.push(callback);
  };

  public websocketConnected = (id: string) => {
    const websocket = this.websockets[id];
    if (websocket) {
      return websocket.connected;
    }
    return false;
  };

  get authHeader() {
    if (this.authToken) {
      return `Bearer ${this.authToken}`;
    } else {
      return "";
    }
  }

  get websocketsConnected() {
    return Object.keys(this.websockets).every(id => this.websocketConnected(id));
  }

  get loading() {
    return this.authLoading || this.loadingRequests.size > 0;
  }

  get authLoading() {
    return this.authIsLoading;
  }

  set authLoading(value) {
    this.authIsLoading = value;
    this.stateChanged();
  }

  public startWebsocket = async (id: string, endpoint: string, handler: IWebsocketHandler) => {
    await this.authIfNeeded();

    this.stopWebsocket(id);

    let url = this.websocketUrl + endpoint;
    url += `${url.includes("?") ? "&" : "?"}auth_token=${this.authToken}`;
    const socket = new Websocket(url, handler, this.stateChanged);
    this.websockets[id] = socket;
  };

  public stopWebsocket = (id: string) => {
    const websocket = this.websockets[id];
    if (websocket) {
      websocket.destroy();
      delete this.websockets[id];
    }
  };

  public async request<ResponseType = any>(
    endpoint: string,
    // Standard request options
    options: RequestInit = {},
    // Additional flight deck request options
    {
      desiredLevel = auth_pb.Auth.AccessLevel.GUEST,
      additionalAcceptedLevels = [auth_pb.Auth.AccessLevel.PILOT, auth_pb.Auth.AccessLevel.ADMIN],
      commandeer = false,
      // The .deserializeBinary of the protobuf message return type
      protobufDeserializer = undefined,
      // Return the response without any parsing
      raw = false,
    }: FlightDeckRequestOptions<ResponseType> = {}
  ): Promise<ResponseType> {
    // NOTE(rachel): make sure this.authHeader is set if needed before creating options
    await this.authIfNeeded(desiredLevel, additionalAcceptedLevels, commandeer);

    options = this.requestOptions(options);

    const requestId = this.setRequestLoading();

    try {
      if (raw) {
        return this.fetchEndpoint(endpoint, options) as unknown as any;
      }
      let data = await this.fetchAndParse(endpoint, options);
      if (protobufDeserializer) {
        return protobufDeserializer(data.getValue_asU8());
      }
      return data;
    } catch (error: any) {
      if (
        error.type === "AuthenticationFailure" ||
        error.type === "PilotAccessRevokedError" ||
        error.type === "PermissionsError"
      ) {
        if (error.extraDataProto) {
          const authRes = auth_pb.Auth.deserializeBinary(error.extraDataProto.getValue_asU8());
          this.setAuth(authRes);
        } else if (error.extraDataJson) {
          const authRes = new auth_pb.Auth();
          authRes.setAccessToken(error.extraDataJson.accessToken);
          authRes.setAccessLevel(
            auth_pb.Auth.AccessLevel[
              error.extraDataJson.accessLevel as keyof typeof auth_pb.Auth.AccessLevel
            ]
          );
          this.setAuth(authRes);
        } else {
          this.clearAuth();
        }

        // retry authenticate
        await this.authIfNeeded(desiredLevel, additionalAcceptedLevels, commandeer);

        // need to use new auth just in case
        (options.headers as Record<string, string>).Authorization = this.authHeader;
        return await this.fetchAndParse(endpoint, options);
      } else {
        throw error;
      }
    } finally {
      this.stopRequestLoading(requestId);
    }
  }

  public async requestRaw(
    endpoint: string,
    // Standard request options
    options: RequestInit = {},
    // Additional flight deck request options
    flightDeckOptions: Omit<FlightDeckRequestOptions<Response>, "raw" | "protobufDeserializer"> = {}
  ) {
    return this.request<Response>(endpoint, options, { ...flightDeckOptions, raw: true });
  }

  public clearAuth = () => {
    this.authToken = null;
    this.authLevel = auth_pb.Auth.AccessLevel.NO_AUTH;
    this.stateChanged();
  };

  public authenticate = async (
    level: ValueType<auth_pb.Auth.AccessLevelMap> = auth_pb.Auth.AccessLevel.GUEST,
    commandeer: boolean = false,
    forceCheck: boolean = false // force it to hit the authentication endpoint again
  ) => {
    // someone else is doing auth already
    while (this.authLoading) {
      // NOTE(roshan): this seems unclean but I couldn't find a better way to 'join' on an
      // in-progress request
      await sleep(20);
    }
    this.authLoading = true;

    if (this.authLevel >= level && this.authToken && !forceCheck) {
      // level is set to what we want, just return
      this.authLoading = false;
      return;
    }

    try {
      const authRequest = new auth_pb.AuthRequest();
      authRequest.setRequestedLevel(level);
      authRequest.setClientId(this.clientId);
      authRequest.setCommandeer(commandeer);
      if (this.credentials) {
        authRequest.setCredentials(this.credentials);
      }

      const response = await fetch(
        `${this.url}/api/authentication`,
        this.requestOptions({
          method: "POST",
          body: authRequest.serializeBinary(),
        })
      );
      const data = await parseResponse(response);
      const authRes = auth_pb.Auth.deserializeBinary(parseData(data).getValue_asU8());
      this.setAuth(authRes);
    } catch (e) {
      throw e;
    } finally {
      this.authLoading = false;
    }
  };

  public checkLevel(
    desiredLevel: ValueType<auth_pb.Auth.AccessLevelMap>,
    additionalAcceptedLevels: ValueType<auth_pb.Auth.AccessLevelMap>[] = []
  ) {
    return this.authLevel === desiredLevel || additionalAcceptedLevels.includes(this.authLevel);
  }

  public authIfNeeded = async (
    desiredLevel = auth_pb.Auth.AccessLevel.GUEST as ValueType<auth_pb.Auth.AccessLevelMap>,
    additionalAcceptedLevels = [
      auth_pb.Auth.AccessLevel.PILOT,
      auth_pb.Auth.AccessLevel.ADMIN,
    ] as ValueType<auth_pb.Auth.AccessLevelMap>[],
    commandeer: boolean = false
  ) => {
    if (!this.checkLevel(desiredLevel, additionalAcceptedLevels)) {
      await this.authenticate(desiredLevel, commandeer);
      if (!this.checkLevel(desiredLevel, additionalAcceptedLevels)) {
        throw Error("Could not authenticate with desired level");
      }
    }
  };

  public getSimulatorRunModes = async (): Promise<ISimulatorRunModesResponse> => {
    const response: unknown = await this.request("/api/runmode/simulator", {
      headers: { Accept: "application/json" },
    });
    if (isSimulatorRunModesResponse(response)) {
      return response;
    }
    // if we get a bad response, log it for inspection, and then error
    console.warn(response);
    throw Error("Could not fetch simulator runmodes");
  };

  public restartRunMode = async (
    runModeName: string,
    commandeer?: boolean
  ): Promise<runmode_pb.RunMode> => {
    if (!runModeName) {
      throw Error("Must specify runmode name");
    }
    const runModeRequest = new runmode_pb.RunModeRequest();
    runModeRequest.setRunModeName(runModeName);
    runModeRequest.setAction(runmode_pb.RunModeAction.TERMINATE_AND_START);
    return await this.request(
      "/api/runmode",
      {
        method: "POST",
        body: runModeRequest.serializeBinary(),
      },
      {
        desiredLevel: auth_pb.Auth.AccessLevel.PILOT,
        additionalAcceptedLevels: [auth_pb.Auth.AccessLevel.ADMIN],
        commandeer,
        protobufDeserializer: runmode_pb.RunMode.deserializeBinary,
      }
    );
  };

  protected setRequestLoading = () => {
    const requestId = Math.random().toString(12).substr(2, 5);
    this.loadingRequests.add(requestId);
    this.stateChanged();
    return requestId;
  };

  protected stopRequestLoading = (requestId: string) => {
    this.loadingRequests.delete(requestId);
    this.stateChanged();
  };

  private setAuth = (authRes: auth_pb.Auth) => {
    if (authRes.getAccessLevel() === auth_pb.Auth.AccessLevel.NO_AUTH) {
      return this.clearAuth();
    }
    this.authToken = authRes.getAccessToken();
    this.authLevel = authRes.getAccessLevel();
    this.stateChanged();

    // if any websockets disconnected due to bad permissions try reconnecting them
    Object.values(this.websockets).forEach(socket => {
      if (socket.permissionsErrorNoReconnect && !socket.connected) {
        socket.permissionsErrorNoReconnect = false;
        socket.reconnect();
      }
    });
  };

  public setRequestHeaders = (headers: RequestInit["headers"]) => {
    this.requestHeaders = convertHeaders(headers);
  };

  private requestOptions = (options: RequestInit): RequestInit => {
    options = options || {};
    options.mode = "cors";
    let headers = {
      ...convertHeaders(options.headers),
      ...this.requestHeaders,
    };
    if (!headers["Content-Type"]) {
      headers["Content-Type"] = "application/x-protobuf";
    }
    if (!headers.Accept) {
      headers.Accept = "application/x-protobuf";
    }
    headers.Authorization = this.authHeader;
    options.headers = headers;
    return options;
  };

  private fetchEndpoint = async (endpoint: string, options: RequestInit) => {
    return await window?.fetch(this.url + endpoint, options);
  };

  private fetchAndParse = async (endpoint: string, options: RequestInit) => {
    const response = await this.fetchEndpoint(endpoint, options);
    const data = await parseResponse(response);
    return parseData(data);
  };

  private stateChanged = () => {
    this.stateChangedCallbacks.forEach(callback => callback(this));
  };
}

const convertHeaders = (incomingHeaders: RequestInit["headers"]) => {
  if (Array.isArray(incomingHeaders)) {
    return (incomingHeaders as [string, string][]).reduce(
      (headerMap, [key, value]) => {
        headerMap[key] = value;
        return headerMap;
      },
      {} as Record<string, string>
    );
  }
  return (incomingHeaders || {}) as Record<string, string>;
};

type IFlightDeckURLInfo = Pick<
  iot_management_pb.WebTunnelConnectionInfo.AsObject,
  "port" | "hostname" | "insecure"
>;

/**
 * Create a flight_deck url from config, without any prefix
 * @param config flight_deck client configuration
 */
export const flightDeckUrlNoPrefix = (config: IFlightDeckURLInfo): string => {
  const port = config.port ? `:${config.port}` : "";
  const urlNoPrefix = `${config.hostname}${port}`;

  return urlNoPrefix;
};

/**
 * Create a flight_deck url from config, with http[s] prefix
 * @param config flight_deck client configuration
 */
export const flightDeckUrlHttp = (config: IFlightDeckURLInfo): string => {
  const urlNoPrefix = flightDeckUrlNoPrefix(config);
  const prefixHttp = config.insecure ? "http://" : "https://";
  return `${prefixHttp}${urlNoPrefix}`;
};

/**
 * Create a flight_deck url from config, with ws[s] prefix
 * @param config flight_deck client configuration
 */
export const flightDeckUrlWs = (config: IFlightDeckURLInfo): string => {
  const urlNoPrefix = flightDeckUrlNoPrefix(config);
  const prefixWs = config.insecure ? "ws://" : "wss://";
  return `${prefixWs}${urlNoPrefix}`;
};
